import type { PreviewKind } from "./catalogTypes";

// One row in the structured answer-options editor.
export type QuestionOptionOverride = {
  label: string;
  value: string;
  helpText?: string;
};

// Per-question override stored in the draft. Covers everything a SysAdmin can
// edit per selected question. The catalog-display helper (getQuestionDisplayMeta)
// reads a subset; selfAssessmentPrompt and helpText flow through into the CSV
// export and import path. `options` is the structured editor's source of truth
// when set; `optionsText` is legacy (line-based editor) and only kept so older
// drafts in localStorage can be migrated on load.
export type QuestionOverride = {
  title?: string;
  prompt?: string;
  selfAssessmentPrompt?: string;
  helpText?: string;
  options?: QuestionOptionOverride[];
  /** @deprecated Use `options`. Migrated on load by draft.ts. */
  optionsText?: string;
  type?: PreviewKind;
};

export type AssessmentSettings = {
  title: string;
  description: string;
  requireSelfAssessment: boolean;
  creationDurationHours: number;
  expirationDurationHours: number;
};

export const DEFAULT_ASSESSMENT_SETTINGS: AssessmentSettings = {
  title: "",
  description: "",
  requireSelfAssessment: false,
  creationDurationHours: 72,
  expirationDurationHours: 168
};

export type InstrumentDraft = {
  id: string;
  title: string;
  description: string;
  /**
   * If set, this instrument is being REUSED from an existing DB row.
   * Description and questions are inherited and locked. Cleared the moment
   * the user edits the title to a non-matching value.
   */
  sourceInstrumentId?: string;
  /**
   * If set, this instrument's assessment is being REUSED from an existing DB
   * row (under sourceInstrumentId). Description + settings are locked.
   */
  sourceAssessmentId?: string;
  /** Canonical, tab-separated rows (one activity per line). All views derive from this. */
  activitiesPasteText: string;
  /** Parser hint for the next clipboard paste in the Paste view. */
  pasteSep: "tab" | "comma" | "table";
  assessmentSettings: AssessmentSettings;
};

// `version: 3` is a DATA-schema marker stamped on every saved draft. v3 added:
// targetNetworkId / targetSponsorId on the module, and sourceInstrumentId /
// sourceAssessmentId on each instrument. Older drafts in localStorage are
// merged onto the current shape via field-level defaults in loadDraft, so
// changing this number alone is no longer load-breaking. Update it as a hint
// to anyone reading saved JSON.
export type ModuleBuilderDraft = {
  version: 3;
  moduleTitle: string;
  moduleDescription: string;
  /** Network the module will be imported under. Required for export. */
  targetNetworkId?: string;
  /** Resolved sponsor for the chosen network — drives reuse scoping. */
  targetSponsorId?: string;
  instruments: InstrumentDraft[];
  activeInstrumentId: string;
  instrumentQuestionIdsById: Record<string, string[]>;
  instrumentQuestionRequiredById: Record<string, Record<string, boolean>>;
  instrumentQuestionAnswerSetById: Record<string, Record<string, string>>;
  instrumentQuestionOverridesById: Record<string, Record<string, QuestionOverride>>;
};

export type ReadinessItem = {
  text: string;
  status: "ok" | "warn" | "bad";
  tag: string;
};
